import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Check, Download, FileJson, ArrowRight, Home, AlertTriangle, ExternalLink, Plus, Trash2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, Skeleton } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import {
  DEFAULT_REMINDER_OFFSETS,
  BRAND,
  STRIPE_CONNECT_ENABLED,
  STATIC_PAYMENT_LINK_PRESETS,
  generateId,
  getSupportContactLabel,
  type StaticPaymentLink,
} from '@maintenance-tracker/shared';
import { api } from '@/lib/utils';
import { useLegalIdentityDisplay } from '@/lib/public-config';
import { useSupport } from '@/lib/support-context';

export { BillingPage } from '@/legacy/pages/billing';

interface EmailTemplate {
  templateKey: string;
  label: string;
  subject: string;
  bodyHtml: string;
  isCustom: boolean;
}

export function SettingsPage() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api<{ workspace: { name: string; timezone: string }; settings: Record<string, string> }>('/api/v1/settings'),
  });

  const [name, setName] = useState('');
  const [offsets, setOffsets] = useState([...DEFAULT_REMINDER_OFFSETS].join(', '));

  const save = useMutation({
    mutationFn: () => api('/api/v1/settings', { method: 'PATCH', body: JSON.stringify({ name: name || data?.workspace?.name }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  });

  const saveReminders = useMutation({
    mutationFn: () =>
      api('/api/v1/settings/reminders', {
        method: 'PATCH',
        body: JSON.stringify({
          default_reminder_offsets: offsets.split(',').map((n) => parseInt(n.trim(), 10)).filter((n) => !Number.isNaN(n)),
        }),
      }),
  });

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader title="Settings" description="Manage your business profile, reminders, and data." />

      <Card title="Business profile" description="Shown on reminder emails and in your account.">
        <div className="space-y-4">
          <Input label="Business name" defaultValue={data?.workspace?.name} onChange={(e) => setName(e.target.value)} />
          <Input label="Timezone" value={data?.workspace?.timezone ?? ''} helper="Reminders send between 9 AM – 6 PM in this timezone." disabled />
          <div className="flex justify-end">
            <Button onClick={() => save.mutate()} loading={save.isPending}>Save changes</Button>
          </div>
        </div>
      </Card>

      <Card title="Reminder schedule" description="When renewal reminders are sent, relative to the end date.">
        <Input
          label="Days before end date"
          helper="Comma-separated. Default: -30, -14, -7, -1"
          value={offsets}
          onChange={(e) => setOffsets(e.target.value)}
        />
        <div className="mt-4 flex justify-end">
          <Button variant="secondary" onClick={() => saveReminders.mutate()} loading={saveReminders.isPending}>
            Save schedule
          </Button>
        </div>
      </Card>

      <Card title="Integrations" description="Import from a spreadsheet today. Live QuickBooks, Jobber, and Housecall Pro connections are coming soon.">
        <Link to="/settings/integrations">
          <Button variant="secondary">
            Manage integrations <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      </Card>

      <Card title="Email templates" description="Customize renewal email copy with merge tags.">
        <Link to="/settings/templates">
          <Button variant="secondary">
            Manage templates <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      </Card>

      <Card title="Export data" description="Download your customers, agreements, and reminder log.">
        <div className="flex flex-wrap gap-2">
          <a href="/api/v1/settings/export?format=csv">
            <Button variant="secondary">
              <Download className="h-4 w-4" /> Export CSV
            </Button>
          </a>
          <a href="/api/v1/settings/export?format=json">
            <Button variant="secondary">
              <FileJson className="h-4 w-4" /> Export JSON
            </Button>
          </a>
        </div>
      </Card>

      <Card title="Danger zone" className="border-danger/30">
        <DeleteAccountForm businessName={data?.workspace?.name ?? ''} />
      </Card>
    </div>
  );
}

function DeleteAccountForm({ businessName }: { businessName: string }) {
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-lg bg-danger-subtle p-3.5">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-danger" />
        <p className="text-[13px] text-[#991B1B]">
          Permanently delete your workspace and all data. This cannot be undone.
        </p>
      </div>
      <Input
        label={`Type "${businessName}" to confirm`}
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
      />
      <div className="flex justify-end">
        <Button
          variant="destructive"
          disabled={confirm !== businessName}
          loading={loading}
          onClick={async () => {
            setLoading(true);
            await api('/api/v1/settings/account', { method: 'DELETE', body: JSON.stringify({ confirm_name: confirm }) });
          }}
        >
          Delete account
        </Button>
      </div>
    </div>
  );
}

interface PaymentsSettings {
  stripe_connect_enabled: boolean;
  connected: boolean;
  stripe_account_id: string | null;
  static_payment_links: StaticPaymentLink[];
  default_static_payment_link_id: string | null;
  has_payment_links: boolean;
  default_payment_links_enabled: boolean;
  plan_allows_payment_links: boolean;
  plan_allows_auto_renew: boolean;
}

export function PaymentsPage() {
  const qc = useQueryClient();
  const search = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');

  const { data, isLoading } = useQuery({
    queryKey: ['payments-settings'],
    queryFn: () => api<PaymentsSettings>('/api/v1/settings/payments'),
  });

  const [links, setLinks] = useState<StaticPaymentLink[]>([]);
  const [defaultId, setDefaultId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!data) return;
    setLinks(data.static_payment_links ?? []);
    setDefaultId(data.default_static_payment_link_id ?? data.static_payment_links?.[0]?.id ?? null);
    setDirty(false);
  }, [data]);

  const saveLinks = useMutation({
    mutationFn: () =>
      api('/api/v1/settings/payments', {
        method: 'PATCH',
        body: JSON.stringify({
          static_payment_links: links.filter((l) => l.url.trim()),
          default_static_payment_link_id: defaultId,
        }),
      }),
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['payments-settings'] });
    },
  });

  const saveDefault = useMutation({
    mutationFn: (enabled: boolean) =>
      api('/api/v1/settings/payments', {
        method: 'PATCH',
        body: JSON.stringify({ default_payment_links_enabled: enabled }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payments-settings'] }),
  });

  const connect = async () => {
    const res = await api<{ url: string }>('/api/v1/settings/payments/connect-url');
    if (res.url) window.location.href = res.url;
  };

  const addPreset = (provider: (typeof STATIC_PAYMENT_LINK_PRESETS)[number]['provider']) => {
    const preset = STATIC_PAYMENT_LINK_PRESETS.find((p) => p.provider === provider);
    if (!preset) return;
    const id = generateId().slice(0, 12);
    const next = [...links, { id, provider: preset.provider, label: preset.label, url: '' }];
    setLinks(next);
    if (!defaultId) setDefaultId(id);
    setDirty(true);
  };

  const updateLink = (id: string, patch: Partial<StaticPaymentLink>) => {
    setLinks((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
    setDirty(true);
  };

  const removeLink = (id: string) => {
    setLinks((prev) => prev.filter((l) => l.id !== id));
    if (defaultId === id) setDefaultId(null);
    setDirty(true);
  };

  const connectedMsg = search.get('connected') === '1';
  const errorMsg = search.get('error');
  const savedLinks = links.filter((l) => l.url.trim());
  const canEnableReminders = savedLinks.length > 0 || (STRIPE_CONNECT_ENABLED && data?.connected);

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader
        title="Renewal page links"
        description="Optional URLs to your own renewal or invoice page — included in reminder emails. Renewal Drive does not process customer payments; funds stay on your existing tools."
      />

      {connectedMsg && STRIPE_CONNECT_ENABLED && (
        <div className="rounded-lg bg-success-subtle px-4 py-3 text-sm text-[#166534]">
          Stripe connected successfully.
        </div>
      )}
      {errorMsg && STRIPE_CONNECT_ENABLED && (
        <div className="rounded-lg bg-danger-subtle px-4 py-3 text-sm text-[#991B1B]">
          Could not connect Stripe ({errorMsg.replace(/_/g, ' ')}). Try again or contact support.
        </div>
      )}

      {!data?.plan_allows_payment_links && !isLoading && (
        <div className="rounded-lg border border-border bg-bg-muted px-4 py-3 text-sm text-text-secondary">
          Optional renewal page links in reminder emails are included on <strong>Starter</strong> and above. Upgrade in Billing to turn this on.
        </div>
      )}

      <Card
        title="Links for renewal reminders"
        description="Paste a URL to a page you already use (your website, invoice portal, or booking page). Customers complete renewal on your process — we only include the link in emails."
      >
        {isLoading ? (
          <Skeleton className="h-40 rounded-lg" />
        ) : (
          <div className="space-y-5">
            <div>
              <p className="mb-2 text-[13px] font-medium text-text-secondary">Quick add</p>
              <div className="flex flex-wrap gap-2">
                {STATIC_PAYMENT_LINK_PRESETS.map((preset) => (
                  <Button
                    key={preset.provider}
                    variant="secondary"
                    size="sm"
                    disabled={!data?.plan_allows_payment_links || links.some((l) => l.provider === preset.provider && !l.url.trim())}
                    onClick={() => addPreset(preset.provider)}
                  >
                    <Plus className="h-3.5 w-3.5" /> {preset.label}
                  </Button>
                ))}
              </div>
            </div>

            {links.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border bg-bg-muted px-4 py-6 text-center text-sm text-text-secondary">
                Add at least one renewal page URL so reminder emails can include a &quot;Renew now&quot; button.
              </p>
            ) : (
              <ul className="space-y-4">
                {links.map((link) => {
                  const preset = STATIC_PAYMENT_LINK_PRESETS.find((p) => p.provider === link.provider);
                  return (
                    <li key={link.id} className="rounded-xl border border-border bg-bg-muted p-4">
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-text-primary">{link.label}</span>
                          <span className="rounded-full bg-bg-surface px-2 py-0.5 text-[11px] uppercase tracking-wide text-text-muted">
                            {link.provider}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="flex cursor-pointer items-center gap-1.5 text-[13px] text-text-secondary">
                            <input
                              type="radio"
                              name="default-payment-link"
                              checked={defaultId === link.id}
                              onChange={() => {
                                setDefaultId(link.id);
                                setDirty(true);
                              }}
                            />
                            <Star className={`h-3.5 w-3.5 ${defaultId === link.id ? 'fill-brand text-brand' : ''}`} />
                            Default for reminders
                          </label>
                          <Button variant="ghost" size="sm" onClick={() => removeLink(link.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                      <Input
                        label="Display name"
                        value={link.label}
                        onChange={(e) => updateLink(link.id, { label: e.target.value })}
                      />
                      <div className="mt-3">
                        <Input
                          label="Payment URL"
                          placeholder={preset?.placeholder ?? 'https://…'}
                          value={link.url}
                          onChange={(e) => updateLink(link.id, { url: e.target.value })}
                          helper={preset?.hint}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
              <p className="text-[13px] text-text-secondary">
                {savedLinks.length
                  ? `${savedLinks.length} link${savedLinks.length === 1 ? '' : 's'} ready · default used in {{payment_link}} emails`
                  : 'Paste your shop’s checkout URL — the same link works for every renewal.'}
              </p>
              <Button
                onClick={() => saveLinks.mutate()}
                loading={saveLinks.isPending}
                disabled={!dirty || !data?.plan_allows_payment_links}
              >
                Save payment links
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card title="Reminder defaults" description="Control whether new agreements include a pay link in campaign emails.">
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-border-strong"
            checked={data?.default_payment_links_enabled ?? true}
            disabled={!canEnableReminders || !data?.plan_allows_payment_links || saveDefault.isPending}
            onChange={(e) => saveDefault.mutate(e.target.checked)}
          />
          <span>
            <span className="block text-sm font-medium text-text-primary">Include payment link on new agreements</span>
            <span className="mt-0.5 block text-[13px] text-text-secondary">
              {canEnableReminders
                ? 'Renewal and win-back emails will include your default payment link when the template uses {{payment_link}}.'
                : 'Save at least one payment link above to enable this.'}
            </span>
          </span>
        </label>
        <p className="mt-4 text-[13px] text-text-muted">
          Tip: Override the link on any agreement from the agreement drawer — useful for custom invoice amounts or member-specific checkout pages.
        </p>
      </Card>

      {STRIPE_CONNECT_ENABLED && (
        <Card title="Stripe Connect (optional)" description={`Hosted renewal checkout — ${BRAND.name} never holds renewal funds.`}>
          {isLoading ? (
            <Skeleton className="h-20 rounded-lg" />
          ) : data?.connected ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-success">
                <Check className="h-4 w-4" /> Connected
              </div>
              <p className="font-mono text-xs text-text-muted">{data.stripe_account_id}</p>
              <Button variant="secondary" onClick={connect}>
                Reconnect Stripe <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-text-secondary">
                Optional: per-agreement hosted checkout with card-on-file auto-renew (Growth+).
              </p>
              <Button onClick={connect} disabled={!data?.plan_allows_payment_links}>
                Connect with Stripe <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

const MERGE_TAGS = ['customer_name', 'plan_name', 'amount', 'end_date', 'payment_link', 'business_name', 'business_phone'];

export function TemplatesPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => api<{ templates: EmailTemplate[] }>('/api/v1/settings/templates'),
  });

  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null);
  const [saved, setSaved] = useState(false);

  const templates = data?.templates ?? [];
  const active = templates.find((t) => t.templateKey === activeKey) ?? templates[0] ?? null;

  // Seed the editor when templates load or the selected template changes.
  useEffect(() => {
    if (!active) return;
    if (activeKey === null) setActiveKey(active.templateKey);
    setSubject(active.subject);
    setBodyHtml(active.bodyHtml);
  }, [active, activeKey]);

  // Debounced live preview.
  useEffect(() => {
    if (!subject && !bodyHtml) return;
    const t = setTimeout(() => {
      api<{ subject: string; html: string }>('/api/v1/settings/templates/preview', {
        method: 'POST',
        body: JSON.stringify({ subject, body_html: bodyHtml }),
      })
        .then(setPreview)
        .catch(() => setPreview(null));
    }, 350);
    return () => clearTimeout(t);
  }, [subject, bodyHtml]);

  const save = useMutation({
    mutationFn: () =>
      api('/api/v1/settings/templates', {
        method: 'PUT',
        body: JSON.stringify({
          template_key: active?.templateKey,
          subject,
          body_html: bodyHtml,
        }),
      }),
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      qc.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl space-y-5">
        <PageHeader title="Email templates" description="Customize the renewal reminders your customers receive." />
        <Skeleton className="h-80 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <PageHeader title="Email templates" description="Customize the renewal reminders your customers receive." />

      <div className="flex flex-wrap gap-2">
        {templates.map((t) => (
          <button
            key={t.templateKey}
            onClick={() => setActiveKey(t.templateKey)}
            className={`rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors ${
              active?.templateKey === t.templateKey
                ? 'border-brand bg-brand-subtle text-brand'
                : 'border-border text-text-secondary hover:text-text-primary'
            }`}
          >
            {t.label}
            {t.isCustom && <span className="ml-1.5 text-[10px] text-brand">• edited</span>}
          </button>
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card title="Edit" description="Use merge tags like {{customer_name}} to personalize each message.">
          <div className="space-y-4">
            <Input label="Subject line" value={subject} onChange={(e) => setSubject(e.target.value)} />
            <div className="flex flex-col gap-1.5">
              <label className="text-[13px] font-medium text-text-primary">Email body (HTML)</label>
              <textarea
                value={bodyHtml}
                onChange={(e) => setBodyHtml(e.target.value)}
                rows={10}
                className="scroll-slim w-full rounded-lg border border-border-strong bg-bg-surface p-3 font-mono text-[13px] text-text-primary shadow-xs focus-visible:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30"
              />
            </div>
            <div>
              <p className="mb-2 text-[12px] font-medium text-text-secondary">Merge tags (click to copy)</p>
              <div className="flex flex-wrap gap-1.5">
                {MERGE_TAGS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => navigator.clipboard?.writeText(`{{${t}}}`)}
                    className="rounded-md border border-border bg-bg-subtle px-2 py-1 text-[12px] text-text-primary hover:border-brand"
                  >
                    {`{{${t}}}`}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => save.mutate()} loading={save.isPending}>Save template</Button>
              {saved && <span className="text-[13px] text-success">Saved</span>}
            </div>
          </div>
        </Card>

        <Card title="Preview" description="Rendered with sample customer data.">
          {preview ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-border bg-bg-muted px-3 py-2">
                <p className="text-[11px] uppercase tracking-wider text-text-muted">Subject</p>
                <p className="text-sm font-medium text-text-primary">{preview.subject}</p>
              </div>
              <iframe
                title="Email preview"
                srcDoc={preview.html}
                className="h-[420px] w-full rounded-lg border border-border bg-white"
              />
            </div>
          ) : (
            <Skeleton className="h-[460px] rounded-lg" />
          )}
        </Card>
      </div>
    </div>
  );
}

export function HelpPage() {
  const { openSupport } = useSupport();
  const display = useLegalIdentityDisplay();
  const supportLabel = getSupportContactLabel(display);

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <PageHeader title="Help &amp; support" description="We're a quick message away." />
      <Card title="We build with you">
        <p className="text-sm text-text-secondary">
          Renewal Drive is built for service shops like yours. If something is missing, awkward, or would save you time
          day-to-day, tell us — we add and adjust features based on what owners actually need.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={() => openSupport('feature_idea', { mode: 'idea' })}>Suggest a feature</Button>
          <Button variant="secondary" onClick={() => openSupport('help_page')}>
            Get help
          </Button>
        </div>
      </Card>
      <Card title="Get help">
        <p className="text-sm text-text-secondary">
          Use the Help button in the bottom-right corner for import help, billing questions, or anything that is not
          working as expected.
        </p>
      </Card>
      <Card title="Email us directly">
        <p className="text-sm text-text-secondary">
          For urgent or detailed requests, contact{' '}
          <a href={`mailto:${BRAND.supportEmail}`} className="font-medium text-brand hover:underline">
            {supportLabel}
          </a>
          {display === 'full' ? (
            <>
              {' '}
              at{' '}
              <a href={`mailto:${BRAND.supportEmail}`} className="font-medium text-brand hover:underline">
                {BRAND.supportEmail}
              </a>
            </>
          ) : null}
          . We typically reply within one business day.
        </p>
      </Card>
    </div>
  );
}

export function NotFoundPage() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-bg-app px-4 text-center">
      <p className="text-[72px] font-semibold leading-none tracking-tight text-brand/20">404</p>
      <div>
        <h1 className="text-xl font-semibold">Page not found</h1>
        <p className="mt-1.5 text-sm text-text-secondary">The page you're looking for doesn't exist or has moved.</p>
      </div>
      <Link to="/">
        <Button>
          <Home className="h-4 w-4" /> Back home
        </Button>
      </Link>
    </div>
  );
}
